// [VP-NOTIF-BADGE 2026-05-28]
//
// Poll the backend for the current user's unread notification count and expose
// it to the navigation so the "My Account" item can show a red dot + count
// from any page. Hides the badge while logged out; resets on the 'logout' event.
//
// Tune POLL_INTERVAL_MS to taste — single knob for refresh latency.
//
// ROLLBACK: `grep -rn "VP-NOTIF-BADGE" bbox/src`.

import { useEffect, useState, useCallback } from 'react';
import { authFetch } from './api';
import { getCookie } from './cookies';

// ▶ How often to refresh the unread count (ms). 60s is a good default.
const POLL_INTERVAL_MS = 60 * 1000;

export const useUnreadNotifications = () => {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    if (!getCookie('access_token')) return; // not logged in — nothing to fetch
    try {
      const data = await authFetch('/notifications/inbox/unread-count');
      setCount(Number(data?.count) || 0);
    } catch {
      // Silent: a transient network hiccup shouldn't disturb the user.
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    const onLogout = () => setCount(0);
    // [VP-NOTIF-BADGE 2026-05-28] React immediately to read/unread changes anywhere
    // in the app (e.g. NotificationsInbox); no need to wait for the next poll tick.
    const onNotifUpdate = () => refresh();
    window.addEventListener('logout', onLogout);
    window.addEventListener('notifications-updated', onNotifUpdate);
    return () => {
      clearInterval(id);
      window.removeEventListener('logout', onLogout);
      window.removeEventListener('notifications-updated', onNotifUpdate);
    };
  }, [refresh]);

  return { count, refresh };
};
